import React from 'react';
import PropTypes from 'prop-types';
import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';
import Image from 'src/components/Image';

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const EnVersion = ({ availableLanguages }) => {
  return (
    <>
      <h4
        id='candidate-privacy-notice'
        data-magellan-target='candidate-privacy-notice'
      >
        Candidate Privacy Notice (Global)
      </h4>

      <p>Available languages:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Effective Date: February 1, 2024</p>
      <p>
        At Plaid, we understand the importance of privacy and transparency. The
        purpose of this Candidate Privacy Notice (“Notice”) is to explain how
        Plaid Inc. and its subsidiaries, including Plaid Financial Ltd. and
        Plaid, B.V. (collectively, “Plaid”, “we”, “our”, or “us”), collect, use,
        and share personal data and information from and about potential
        candidates (“you” or “your”) for positions with Plaid (“Candidate
        Information”).
      </p>
      <p>
        Please note that if you submit your CV/resume or other Candidate
        Information through our website, we may also collect and process
        information about you in connection with your access to or use of
        Plaid’s website. Additional information regarding our use of your data
        can be found in our Privacy Statement. Further information about the
        types of cookies we use on our website, why we use them, and how you can
        control or limit the use of cookies can be found in our{' '}
        <Link href='#cookie-policy'>
          <a>Cookie Policy</a>
        </Link>
        .
      </p>
      <p>
        <em>Jump to section:</em>
      </p>
      <p>
        <Link href='#candidate-privacy-our-data-practices'>
          <a data-smooth-scroll data-offset='80'>
            Our Data Practices
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#candidate-privacy-information-we-collect'>
              <a>Information We Collect</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#candidate-privacy-how-we-use-your-information'>
              <a>How We Use Your Information</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#candidate-privacy-how-we-disclose-your-information'>
              <a>How We Disclose Your Information</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#candidate-privacy-our-lawful-bases-for-processing'>
              <a>
                Our Lawful Bases for Processing (UK and EEA Candidates Only)
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#candidate-privacy-our-retention-practices'>
              <a>Our Retention Practices</a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#candidate-privacy-other-details'>
          <a data-smooth-scroll data-offset='80'>
            Other Details
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#candidate-privacy-international-data-transfers'>
              <a>International Data Transfers</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#california-candidates-data-protection-rights'>
              <a>California Candidates Data Protection Rights</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#uk-eea-and-canadian-candidates-data-protection-rights'>
              <a>UK, EEA, and Canadian Candidates Data Protection Rights</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#candidate-privacy-changes-to-this-notice'>
              <a>Changes to this Notice</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#candidate-privacy-contacting-plaid'>
              <a>Contacting Plaid</a>
            </Link>
          </p>
        </li>
      </ul>
      <br />
      <br />

      <h4 id='candidate-privacy-our-data-practices'>Our Data Practices</h4>

      <h5 id='candidate-privacy-information-we-collect'>
        Information We Collect
      </h5>
      <p>
        We collect Candidate Information provided by (1) you, including through
        Plaid’s Careers page (available at https://plaid.com/careers/), (2)
        third parties, such as background check providers, recruitment
        consultants or professional recruitment agencies, or (3) Plaid itself
        through candidate research. We may also derive Candidate Information by
        creating a profile reflecting your employment preferences,
        characteristics, behavior, intelligence, abilities, and aptitudes, such
        as through notes taken as part of the interview process.
      </p>
      <p>
        We collect the following categories of Candidate Information about
        candidates we consider for jobs:
      </p>
      <ul>
        <li>
          <p>
            <u>identifiers and contact information</u>, including your full
            name, address, phone number, email address, or other contact
            information;
          </p>
        </li>
        <li>
          <p>
            <u>professional or employment-related information</u>, including
            information in your resume or CV; your work history, certifications,
            professional licenses, and other experience; and contact details of
            your former/current employer (when such details contain your
            personal data);
          </p>
        </li>
        <li>
          <p>
            <u>education information</u>, including information about your
            degrees and training; transcripts; and academic achievements;
          </p>
        </li>
        <li>
          <p>
            <u>social networking and similar information</u>, such as social
            media and public profile information, including URLs related to your
            LinkedIn, Twitter, GitHub, and Portfolio profiles, and your personal
            website;
          </p>
        </li>
        <li>
          <p>
            <u>
              in certain regions (like the US) and as permitted by applicable
              law we may collect demographic and other sensitive information
              (e.g., race, gender, veteran status, ethnicity, sexual
              orientation, and disability status)
            </u>
            ; and
          </p>
        </li>
        <li>
          <p>
            <u>other information you submit</u>, including additional
            information provided in an application form, cover letter, or other
            work product.
          </p>
        </li>
      </ul>

      <p>
        If your application progresses, we will also collect the following
        Candidate Information:
      </p>
      <ul>
        <li>
          <p>
            <u>information collected as part of the interview process</u>, such
            as notes taken from your interview (when they contain your personal
            data); and personal data included in any take-home project;
          </p>
        </li>
        <li>
          <p>
            <u>information necessary to schedule your interview</u>, including
            identifiers such as your address, your Known Traveler Number, and
            any frequent traveler details;
          </p>
        </li>
        <li>
          <p>
            <u>audio and visual information</u>, including photographs for work
            profiles and security footage gathered on Plaid premises.
          </p>
        </li>
        <li>
          <p>
            <u>electronic network activity information and usage data</u>,
            including your use of plaid equipment, devices, software and other
            assets.
          </p>
        </li>
        <li>
          <p>
            <u>details about employment expectations</u>, including, to the
            extent they contain your personal data, type of employment sought,
            compensation expectations, willingness to relocate, and other job
            preferences;
          </p>
        </li>
        <li>
          <p>
            <u>
              (if and to the extent relevant for a specific role) information
              necessary to run a background check
            </u>
            , such as information obtained either from you or through reference
            and background checks, subject to any further permissions or
            notifications required and/or permitted by applicable law, usually
            by way of providing an official Certificate of Good Conduct (for
            instance in the Netherlands: Verklaring Omtrent Gedrag or VOG); and
          </p>
        </li>
        <li>
          <p>
            <u>information required to initiate employment</u>, including proof
            of eligibility to work in a specified country (such as a passport or
            visa); demographic information such as your date and place of birth,
            and citizenship; identifiers such as your social security number
            (where required and/or permitted by law), national insurance number,
            other government-issued identifier, tax identifiers and related tax
            information (where required and/or permitted by law), and bank
            account numbers and related information; benefits eligibility
            information; and other information you provide for the purposes of
            starting employment or executing an employment agreement.
          </p>
        </li>
      </ul>

      <h5 id='candidate-privacy-how-we-use-your-information'>
        How We Use Your Information
      </h5>
      <p>
        We use the Candidate Information collected about you to process your
        application for employment and to comply with statutory obligations we
        have in relation to your application, including confirming your
        eligibility to work in a given location.
      </p>
      <p>
        We will also use your Candidate Information for the following purposes:
      </p>
      <ul>
        <li>
          <p>
            <u>administering and processing your application</u>, including
            verifying your identification, experience and other information you
            submit and, if your application progresses, any interview
            information and background check information;
          </p>
        </li>
        <li>
          <p>
            <u>communicating with you about your application</u>, including
            contacting you via phone, email or social media platforms about your
            application and, if your application progresses, coordinating any
            interview and background check;
          </p>
        </li>
        <li>
          <p>
            <u>
              assessing and evaluating your suitability for the role for which
              you have applied, or for other vacancies
            </u>
            , including verifying your identification, experience and other
            information you submit and, if your application progresses, any
            interview information and background check information;
          </p>
        </li>
        <li>
          <p>
            <u>
              conducting reference and background checks, including criminal
              records checks, as part of your application
            </u>
            , subject to applicable law, including verifying your
            identification, experience and other information you submit and, if
            your application progresses, any interview information and
            background check information;
          </p>
        </li>
        <li>
          <p>
            <u>
              complying with applicable laws and employment-related requirements
            </u>
            , including verifying identification information and information
            required to initiate employment, for purposes such as confirming
            ability to legally work in a specific location, setting up payroll,
            withholdings and benefits, and complying with statutory reporting
            requirements;
          </p>
        </li>
        <li>
          <p>
            <u>improving our recruitment process</u>, as permitted by applicable
            law and only in certain regions (like the US), including by
            generating analytics using Candidate Information to ensure we
            recruit a diverse set of candidates;
          </p>
        </li>
        <li>
          <p>
            <u>informing you of career opportunities</u>, including other
            vacancies for which you may be eligible; and
          </p>
        </li>
        <li>
          <p>
            <u>for other notified purposes with your consent</u>.
          </p>
        </li>
      </ul>
      <p>
        Please note that if you accept employment with Plaid, your Candidate
        Information will become part of your employment record and will be used
        for employment purposes in accordance with applicable law and any
        applicable Personnel Privacy Notice. If you are not accepted for a role,
        however, we may still keep your application, including relevant
        Candidate Information, to allow us to consider you for other career
        opportunities with Plaid and provided that, if required by applicable
        laws, we obtained your prior consent for such longer retention of your
        personal data. If you do not want your information retained in the event
        you are not offered a position, as applicable, please refer to the
        following sections below for options that may be available to you:{' '}
        <Link href='#california-candidates-data-protection-rights'>
          <a>California Candidates Data Protection Rights</a>
        </Link>{' '}
        and{' '}
        <Link href='#uk-eea-and-canadian-candidates-data-protection-rights'>
          <a>UK, EEA, and Canadian Candidates Data Protection Rights</a>
        </Link>
        .
      </p>
      <p>
        Plaid does not use your personal data for automated individual decision
        making (including profiling).
      </p>

      <h5 id='candidate-privacy-how-we-disclose-your-information'>
        How We Disclose Your Information
      </h5>
      <p>
        We disclose your personal data to others as follows, or as otherwise
        described in this Notice:
      </p>
      <ul>
        <li>
          <p>
            <u>
              between and among Plaid and our subsidiaries and affiliated
              entities
            </u>
            , as the specific entity you&#39;re applying to work for is part of
            a wider group of companies with headquarters in the United States
            and offices in various jurisdictions that share human resources and
            IT systems for processing your application, assessing your
            suitability for the role, and initiating employment (if applicable);
          </p>
        </li>
        <li>
          <p>
            <u>with our data processors and other service providers</u>,
            including data analytics service providers, background check service
            providers, and recruitment agencies, in connection with the services
            they perform for us or on our behalf. Where required, these service
            providers, partners, or vendors acting on our behalf will be subject
            to contractual obligations to implement appropriate technical and
            organizational measures to safeguard the Candidate Information, and
            to process it only as instructed.
          </p>
        </li>
        <li>
          <p>
            <u>
              to law enforcement, government authorities, regulators, and other
              third parties
            </u>
            , such as courts and other authorities, independent external
            advisers and internal compliance and investigation teams, if we
            believe such disclosure is in accordance with, or is otherwise
            required by, any applicable law, regulation, or legal process;
          </p>
        </li>
        <li>
          <p>
            <u>
              in connection with, or during the negotiations of, any merger,
              sale of company assets, consolidation or restructuring, financing,
              or acquisition of all or a portion of our business by or into
              another company
            </u>
            , as permitted by applicable law;
          </p>
        </li>
        <li>
          <p>
            <u>as necessary to protect Plaid</u>, including to establish,
            exercise, or defend against potential, threatened or actual legal
            claims;
          </p>
        </li>
        <li>
          <p>
            <u>
              to enforce any contract with you or if we believe that your
              actions are inconsistent with our policies
            </u>
            ;
          </p>
        </li>
        <li>
          <p>
            <u>
              to respond to any claims, to protect our rights or the rights of a
              third party, to protect the safety of any person or to prevent any
              illegal activity
            </u>
            ; or
          </p>
        </li>
        <li>
          <p>
            <u>
              for any other notified purpose, with your consent or at your
              direction
            </u>
            .
          </p>
        </li>
      </ul>
      <p>
        We may also use or disclose aggregated, anonymized, or de-identified
        information that cannot reasonably be used to identify you, to the
        extent permitted under applicable law.
      </p>

      <h5 id='candidate-privacy-our-lawful-bases-for-processing'>
        Our Lawful Bases for Processing (UK and EEA Candidates Only)
      </h5>
      <p>
        For candidates in the United Kingdom (“UK”) and European Economic Area
        (“EEA”), our legal basis for collecting, using, and sharing Candidate
        Information will depend on the information concerned and the context in
        which we collected or processed it. In general, however, we will
        normally only collect and process Candidate Information where:
      </p>
      <p>
        (a) we need to comply with legal obligations applicable to us (such as
        applicable immigration and/or employment laws and regulations);
      </p>
      <p>
        (b) we need the information to fulfill our responsibilities in any
        actual or prospective agreement with you (such as an employment
        agreement or an offer of employment if you are considered for
        employment);
      </p>
      <p>
        (c) the processing is necessary for our legitimate interests and not
        overridden by your data protection interests or fundamental rights and
        freedoms (such as to ensure a safe working environment for all our team
        members, and to ensure the reliability of our candidates, and otherwise
        to administer and process your application, or to communicate with you
        about your application); and
      </p>
      <p>
        (d) we have your consent to do so (in cases where your consent is truly
        freely given).
      </p>
      <p>
        Where we are legally allowed or required to collect and process special
        categories of data (for example, race), then we will only process such
        information:
      </p>
      <ul>
        <li>
          <p>
            where we reasonably need to do so in order to comply with our legal
            obligations as a prospective employer;
          </p>
        </li>
        <li>
          <p>
            where we need to do so in order to be informed of your working
            capacity;
          </p>
        </li>
        <li>
          <p>
            where we need to do so to protect your or another person&#39;s vital
            interests (and we are unable to obtain your consent); or
          </p>
        </li>
        <li>
          <p>
            where you have given us your consent (in cases where your consent is
            truly freely given).
          </p>
        </li>
      </ul>

      <p>
        To the extent we rely on consent to collect and process Candidate
        Information, you have the right to decline your consent or to withdraw
        your consent at any time by contacting us at{' '}
        <Link href='mailto:people-privacy@plaid.com'>
          <a>people-privacy@plaid.com</a>
        </Link>
        . Withdrawing your consent will not, however, affect the lawfulness of
        any processing we conducted prior to your withdrawal, nor will it affect
        the processing of your personal data conducted for other purposes in
        reliance on lawful processing grounds other than consent.
      </p>

      <h5 id='candidate-privacy-our-retention-practices'>
        Our Retention Practices
      </h5>
      <p>
        We retain Candidate Information for no longer than necessary to satisfy
        the purpose for which it was collected and used, as described in this
        Notice, unless a longer retention period is required or permitted under
        applicable law. As noted above, however, if you accept employment with
        Plaid, your Candidate Information will become part of your employment
        record and will be used for employment purposes in accordance with
        applicable law and any applicable Employee Privacy Notice.
      </p>

      <h4 id='candidate-privacy-other-details'>Other Details</h4>

      <h5 id='candidate-privacy-international-data-transfers'>
        International Data Transfers
      </h5>
      <p>
        We operate internationally, and as a result, will transfer Candidate
        Information across international borders, including from the UK, EEA,
        and/or Canada to the United States, for processing and storage. To the
        extent that Candidate Information is transferred from the UK or EEA to
        territories/countries for which the EU Commission and/or Secretary of
        State in the UK (as appropriate) has not made a finding that the legal
        framework in that territory/country provides adequate protection for
        individuals' rights and freedoms for their personal data, we will
        transfer such data consistent with applicable data protection laws,
        including through the use of the EU Commission-approved and/or Secretary
        of State in the UK approved (as appropriate) standard contractual
        clauses (as applicable). You can ask for a copy of these standard
        contractual clauses by contacting us as set out below.
      </p>
      <p>
        In addition, we are committed to ensure that we comply with any
        additional requirements or guidelines from the European Court of
        Justice, data protection law and/or supervisory authorities in relation
        to the transfer of personal data.
      </p>

      <h5 id='california-candidates-data-protection-rights'>
        California Candidates Data Protection Rights
      </h5>
      <p>
        The California Consumer Privacy Act or “CCPA”, as amended by the
        California Privacy Rights Act, affords candidates residing in California
        certain rights with respect to their Candidate Information. If you are a
        California resident, this section applies to you.
      </p>
      <p>
        We collect, or have collected in the preceding 12 months, the categories
        of Candidate Information listed in the below table from the sources
        identified in the “Information We Collect” section. For details about
        the precise data points we collect, or have collected in the preceding
        12 months, please see the “Information We Collect” section above. We use
        and disclose the Candidate Information for the business and commercial
        purposes listed in the following table, and we disclose the Candidate
        Information to the recipients also listed in the following table.
      </p>
      <Image
        src='/assets/img/legal/candidate-privacy-notice/candidate-information-table-en@2x.png'
        alt='Candidate Information table'
        isLazy={false}
      />
      <p>
        We retain the categories of Candidate Information in accordance with the
        retention practices described above in{' '}
        <Link href='#candidate-privacy-our-retention-practices'>
          <a>Our Retention Practices</a>
        </Link>
        .
      </p>
      <p>
        Plaid does not (1) “sell” or “share” (as those terms are defined by the
        CCPA) Candidate Information or (2) use or disclose sensitive personal
        information for any purpose other than what is specified in CCPA section
        1798.121(a).
      </p>
      <p>
        Subject to certain limitations, you have the right to (a) request to
        know more about the sources of and the categories and specific pieces of
        Candidate Information we collect, use, and disclose and the categories
        of third parties to which we disclose such information, (b) request
        deletion of Candidate Information, (c) request correction of inaccurate
        Candidate Information, and (d) not be discriminated against for
        exercising these rights. You may make these requests by emailing us at
        people-privacy@plaid.com completing this{' '}
        <Link href='https://my.plaid.com/data-subject-request-form/'>
          <a>webform</a>
        </Link>{' '}
        or calling (415) 799 - 1354. We will verify your request by asking you
        to provide information related to your recent interactions with us, such
        as your phone number and email address. We will not discriminate against
        you if you exercise your rights under the CCPA.
      </p>
      <p>
        If we receive your request from an authorized agent who does not provide
        a valid power of attorney, we may ask the authorized agent to provide
        proof that you gave the agent signed permission to submit the request to
        exercise rights on your behalf. In the absence of a valid power of
        attorney, we may also require you to verify your own identity directly
        with us or confirm to us that you otherwise provided the authorized
        agent permission to submit the request. If you are an authorized agent
        seeking to make a request, please email us at{' '}
        <Link href='mailto:people-privacy@plaid.com'>
          <a>people-privacy@plaid.com</a>
        </Link>
        .
      </p>

      <h5 id='uk-eea-and-canadian-candidates-data-protection-rights'>
        UK, EEA, and Canadian Candidates Data Protection Rights
      </h5>
      <p>
        Under applicable law, you have certain rights in relation to your
        Candidate Information. These rights can differ by country and province.
      </p>
      <p>
        Subject to certain limitations and exceptions, those rights include the
        right to: (i) know whether we process personal data about you, (ii)
        request access to or rectification of the personal data we process about
        you; (iii) object to, or request that we restrict, the processing of
        your personal data; (iv) request that we delete personal data about you;
        (v) request that we provide the personal data you provided to us in a
        structured, commonly used and machine-readable format, for transmission
        to another controller; (vi) revoke consent at any time where our
        processing is based on your consent; and (vii) lodge a complaint with
        your local supervisory authority (if you are in the UK, please see this{' '}
        <Link href='https://ico.org.uk/global/contact-us/'>
          <a>page</a>
        </Link>{' '}
        for contact details for the UK Information Commissioner's Office and if
        you’re in the EEA, please see this{' '}
        <Link href='https://edpb.europa.eu/about-edpb/board/members_en'>
          <a>page</a>
        </Link>{' '}
        for contact details).
      </p>
      <p>
        If you’d like to exercise any of your rights, where applicable, please
        contact us using the contact information provided below.
      </p>
      <p>
        Additionally, we are happy to provide you with a more detailed overview
        of our relevant processing purposes (as well as the related categories
        of data, retention periods and legal processing grounds) upon your
        request.
      </p>

      <h5 id='candidate-privacy-changes-to-this-notice'>
        Changes to this Notice
      </h5>
      <p>
        We may change this Candidate Privacy Notice from time to time. If we
        make any changes, we will update the effective date at the top of this
        Notice. In addition, if any of the changes are material and impact your
        Candidate Information, we will also send you a copy via email.
      </p>

      <h5 id='candidate-privacy-contacting-plaid'>Contacting Plaid</h5>
      <p>
        If you have any questions about this Candidate Privacy Notice, our
        privacy practices, or the Candidate Information we collect about you, or
        have requests related to such Candidate Information, you can contact us
        at{' '}
        <Link href='mailto:people-privacy@plaid.com'>
          <a>people-privacy@plaid.com</a>
        </Link>{' '}
        or by mail at:
      </p>
      <p>
        <em>
          If you are applying for a job with Plaid Inc. (located in the United
          States):
        </em>
      </p>
      <p>
        Plaid Inc. <br />
        Attn: Legal <br />
        PO Box 7775 #35278 <br />
        San Francisco, California 94120-7775, U.S.A.
      </p>
      <p>
        <em>
          If you are applying for a job with Plaid Financial Ltd. (located in
          the United Kingdom):
        </em>
      </p>
      <p>
        Plaid Financial Ltd. <br />
        Attn: Legal <br />
        New Penderel House, 4th Floor <br />
        283-288 High Holborn
        <br />
        London, United Kingdom, WC1V 7HP
      </p>
      <p>
        <em>
          If you are applying for a job with Plaid, B.V. (located in the
          Netherlands):
        </em>
      </p>
      <p>
        Plaid, B.V. <br />
        Attn: Legal <br />
        Muiderstraat 1 <br />
        1011 PZ Amsterdam, Netherlands
      </p>

      <hr className='section-divider' />
    </>
  );
};

propTypes.EnVersion = PropTypes;
export default EnVersion;
